"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSetErrorMessage = useSetErrorMessage;
exports.useSetWarningMessage = useSetWarningMessage;
exports.useSetInfoMessage = useSetInfoMessage;
exports.useSetSuccessMessage = useSetSuccessMessage;
const react_1 = __importDefault(require("react"));
const withAlerts_1 = require("../contexts/withAlerts");
/**
 * Will set an error message in Exchange.
 *
 * @example
 *
 * ```ts
 * import { useSetErrorMessage } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const setErrorMessage = useSetErrorMessage();
 *
 *   setErrorMessage('An error occurred');
 * };
 * ```
 * @returns A function to set an error message
 */
function useSetErrorMessage() {
    const { setAlert } = react_1.default.useContext(withAlerts_1.AlertsContext);
    function setError(error, options) {
        if (typeof error === 'string') {
            setAlert({
                type: 'error',
                message: error,
                hint: options === null || options === void 0 ? void 0 : options.hint,
                isHTML: options === null || options === void 0 ? void 0 : options.isHTML,
                requestId: options === null || options === void 0 ? void 0 : options.requestId
            });
            return;
        }
        setAlert({
            type: 'error',
            message: error.message,
            hint: options === null || options === void 0 ? void 0 : options.hint,
            isHTML: options === null || options === void 0 ? void 0 : options.isHTML,
            requestId: error.requestId
        });
    }
    return setError;
}
/**
 * Will set a warning message in Exchange.
 *
 * @example
 *
 * ```ts
 * import { useSetWarningMessage } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const setWarningMessage = useSetWarningMessage();
 *
 *   setWarningMessage('Guarda la moto');
 * };
 * ```
 * @returns A function to set a warning message
 */
function useSetWarningMessage() {
    const { setAlert } = react_1.default.useContext(withAlerts_1.AlertsContext);
    return (message, options) => {
        setAlert({
            type: 'warning',
            message,
            hint: options === null || options === void 0 ? void 0 : options.hint,
            isHTML: options === null || options === void 0 ? void 0 : options.isHTML
        });
    };
}
/**
 * Will set an info message in Exchange.
 *
 * @example
 *
 * ```ts
 * import { useSetInfoMessage } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const setInfoMessage = useSetInfoMessage();
 *
 *   setInfoMessage('The info');
 * };
 * ```
 * @returns A function to set an info message
 */
function useSetInfoMessage() {
    const { setAlert } = react_1.default.useContext(withAlerts_1.AlertsContext);
    return (message, options) => {
        setAlert({
            type: 'info',
            message,
            hint: options === null || options === void 0 ? void 0 : options.hint,
            isHTML: options === null || options === void 0 ? void 0 : options.isHTML
        });
    };
}
/**
 * Will set a success message in Exchange.
 *
 * @example
 *
 * ```ts
 * import { useSetSuccessMessage } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const setSuccessMessage = useSetSuccessMessage();
 *
 *   setSuccessMessage('The info');
 * };
 * ```
 * @returns A function to set an info message
 */
function useSetSuccessMessage() {
    const { setAlert } = react_1.default.useContext(withAlerts_1.AlertsContext);
    return (message, options) => {
        setAlert({
            type: 'success',
            message,
            hint: options === null || options === void 0 ? void 0 : options.hint,
            isHTML: options === null || options === void 0 ? void 0 : options.isHTML
        });
    };
}
